/**
 * P-5 Foto-Upload-Queue (IndexedDB).
 *
 * Separate Queue zu Operation-Queue aus P-6: Fotos sind groß und
 * duerfen asynchron nachlaufen. Pflicht-Foto-Flows (Entsorgung,
 * Unknown-Item) rufen `enqueueAndWait` und warten bis synced.
 */

import { getQueueUser, ownsEntry } from "./queueUser";

const DB_NAME = "platzwart_photos";
const DB_VERSION = 1;
const STORE = "photo_upload_queue";

let _dbPromise = null;

function openDb() {
    if (_dbPromise) return _dbPromise;
    _dbPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE)) {
                const store = db.createObjectStore(STORE, { keyPath: "id" });
                store.createIndex("status", "status", { unique: false });
                store.createIndex("photoId", "photoId", { unique: false });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
    return _dbPromise;
}

function tx(mode, fn) {
    return openDb().then(
        (db) =>
            new Promise((resolve, reject) => {
                const t = db.transaction(STORE, mode);
                const s = t.objectStore(STORE);
                const result = fn(s);
                t.oncomplete = () => resolve(result);
                t.onerror = () => reject(t.error);
                t.onabort = () => reject(t.error);
            })
    );
}

function uuid() {
    return crypto.randomUUID
        ? crypto.randomUUID()
        : "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
              const r = (Math.random() * 16) | 0;
              return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
          });
}

export async function listQueue() {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const t = db.transaction(STORE, "readonly");
        const req = t.objectStore(STORE).getAll();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => reject(req.error);
    });
}

/** #53: nur Einträge des angemeldeten Nutzers (UI-Liste). */
export async function listMine() {
    return (await listQueue()).filter(ownsEntry);
}

export async function queueCounts() {
    const all = await listMine();
    const counts = { pending: 0, uploading: 0, failed: 0, synced: 0 };
    for (const j of all) counts[j.status] = (counts[j.status] || 0) + 1;
    counts.active = counts.pending + counts.uploading;
    return counts;
}

export async function enqueueLocal(entry) {
    const id = uuid();
    const rec = {
        id,
        photoId: null,
        status: "pending",
        attempts: 0,
        lastAttemptAt: null,
        lastError: null,
        createdAt: new Date().toISOString(),
        // #53: Eintrag gehört dem angemeldeten Nutzer; nur dessen Sitzung
        // zeigt und versendet ihn (geteilte Lagergeräte).
        userId: getQueueUser(),
        ...entry,
    };
    await tx("readwrite", (s) => s.put(rec));
    return rec;
}

export async function updateEntry(id, patch) {
    return tx("readwrite", (s) => {
        const get = s.get(id);
        get.onsuccess = () => {
            const rec = get.result;
            if (!rec) return;
            Object.assign(rec, patch);
            s.put(rec);
        };
    });
}

export async function removeEntry(id) {
    return tx("readwrite", (s) => s.delete(id));
}

/** Ein hängender Upload ohne Timeout blockiert die Queue und wird nach dem
 *  Stale-Fenster (2 min) fälschlich als tot recovered -> Doppel-Upload.
 *  Der Timeout liegt bewusst UNTER dem Stale-Fenster. */
const UPLOAD_TIMEOUT_MS = 60_000;

/** Upload-Request via fetch (damit multipart geht, ohne axios Overhead). */
async function uploadToServer(rec, apiBase) {
    const fd = new FormData();
    fd.append("entity_type", rec.entityType);
    fd.append("purpose", rec.purpose);
    fd.append("client_upload_id", rec.id);
    // Legacy clients may still send these fields; the server derives evidence
    // and performs entity binding only inside an authenticated workflow.
    fd.append("is_evidence", String(rec.isEvidence ? true : false));
    if (rec.comment) fd.append("comment", rec.comment);
    if (rec.width) fd.append("width", String(rec.width));
    if (rec.height) fd.append("height", String(rec.height));
    if (rec.takenAt) fd.append("taken_at", rec.takenAt);
    fd.append("file", rec.blob, `photo.${rec.ext || "jpg"}`);
    if (rec.thumb) fd.append("thumbnail", rec.thumb, `thumb.${rec.ext || "jpg"}`);

    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), UPLOAD_TIMEOUT_MS);
    let res;
    try {
        res = await fetch(`${apiBase}/photos/upload`, {
            method: "POST",
            credentials: "include",
            body: fd,
            signal: ctrl.signal,
        });
    } catch (e) {
        // Abort/Netzfehler: kein e.status -> syncEntry stuft als retriable ein
        throw e.name === "AbortError" ? new Error("Upload-Timeout") : e;
    } finally {
        clearTimeout(timer);
    }
    if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
            const j = await res.json();
            msg = j.detail || msg;
        } catch (_e) { /* ignore */ }
        const err = new Error(msg);
        err.status = res.status;
        throw err;
    }
    return res.json();
}

/**
 * Sync a single entry. Returns photoId on success or throws.
 */
export async function syncEntry(rec, apiBase) {
    await updateEntry(rec.id, { status: "uploading", lastAttemptAt: new Date().toISOString() });
    try {
        const resp = await uploadToServer(rec, apiBase);
        await updateEntry(rec.id, { status: "synced", photoId: resp.id });
        // Nach Sync: nach 60 Sekunden aus der Queue entfernen (leichtes GC).
        setTimeout(() => { removeEntry(rec.id).catch(() => {}); }, 60_000);
        return resp;
    } catch (e) {
        const retriable = !e.status || (e.status >= 500 && e.status <= 599);
        await updateEntry(rec.id, {
            status: retriable ? "pending" : "failed",
            attempts: (rec.attempts || 0) + 1,
            lastError: e.message,
        });
        throw e;
    }
}

/** R11: nach App-Kill/Reload hängengebliebene 'uploading'-Einträge
 *  zurücksetzen -- der Blob liegt noch in IndexedDB, Upload ist wiederholbar. */
const STALE_UPLOADING_MS = 2 * 60 * 1000;

async function recoverStale(all) {
    const cutoff = Date.now() - STALE_UPLOADING_MS;
    for (const rec of all) {
        if (rec.status !== "uploading") continue;
        const last = rec.lastAttemptAt ? new Date(rec.lastAttemptAt).getTime() : 0;
        if (!last || last < cutoff) {
            await updateEntry(rec.id, { status: "pending" });
            rec.status = "pending";
        }
    }
}

/** Reentrancy-Guard: Worker-Intervall und manuelles "Alle senden" dürfen
 *  nie parallel über dieselben pending-Records laufen (Foto-Upload ist
 *  nicht idempotent -> Duplikate). Läuft bereits ein Pass, wird dessen
 *  Promise zurückgegeben. */
let _passInFlight = null;

/** Poll/Worker: synced pending entries one at a time (Backoff via attempts). */
export function runSyncPass(apiBase) {
    if (_passInFlight) return _passInFlight;
    _passInFlight = (async () => {
        // #53: abgemeldet wird nichts versendet; fremde Einträge bleiben
        // liegen, bis ihr Besitzer sich wieder anmeldet.
        if (!getQueueUser()) return;
        const all = await listQueue();
        await recoverStale(all);
        for (const rec of all) {
            // Alt-Einträge (vor #53) beim ersten Kontakt stempeln.
            if (!rec.userId) {
                await updateEntry(rec.id, { userId: getQueueUser() });
                rec.userId = getQueueUser();
            }
        }
        const pending = all.filter((r) => r.status === "pending" && ownsEntry(r));
        for (const rec of pending) {
            const attempts = rec.attempts || 0;
            const backoffMs = Math.min(30_000 * Math.pow(2, attempts), 600_000);
            const last = rec.lastAttemptAt ? new Date(rec.lastAttemptAt).getTime() : 0;
            if (last && Date.now() - last < backoffMs) continue;
            try {
                await syncEntry(rec, apiBase);
            } catch (_e) {
                // next pass handles retry
            }
        }
    })().finally(() => {
        _passInFlight = null;
    });
    return _passInFlight;
}
